import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { PriorityMessageAdjustUnit, ScheduledTask } from '../../../../hooks/useMessageUsersScheduledSessions';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    scheduledMessages: ScheduledTask[];
    refetchScheduledMessages: () => void;
    bulkAdjustScheduledMessages: (
        scheduledTaskIds: string[],
        offsetAmount: number,
        offsetUnit: PriorityMessageAdjustUnit,
    ) => Promise<{ success: boolean; adjustedCount?: number }>;
    bulkAdjustScheduledMessagesLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'scheduleDateTime';
type SortDirection = 'asc' | 'desc';
type AdjustDirection = 'later' | 'earlier';

const dateFormat = 'DD/MM/YYYY hh:mm A';

const MessageUsersPriorityMessageBulkAdjustDialog = (props: Props) => {
    const { scheduledMessages, refetchScheduledMessages, bulkAdjustScheduledMessages, bulkAdjustScheduledMessagesLoading, open, setOpen } =
        props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedScheduledTaskIds, setSelectedScheduledTaskIds] = useState<string[]>([]);
    const [direction, setDirection] = useState<AdjustDirection>('later');
    const [offsetAmount, setOffsetAmount] = useState<number>(1);
    const [offsetUnit, setOffsetUnit] = useState<PriorityMessageAdjustUnit>('hours');

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('scheduleDateTime');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Signed offset sent to the server: negative moves earlier
    const signedOffset = direction === 'later' ? offsetAmount : -offsetAmount;

    const shiftDate = (date: Date | string): moment.Moment => moment(date).add(signedOffset, offsetUnit);

    // Sort the scheduled tasks based on current sort settings
    const sortedScheduledTasks = useMemo(() => {
        return [...scheduledMessages].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = (a.payload?.name || '').toLowerCase();
                    bValue = (b.payload?.name || '').toLowerCase();
                    break;
                case 'scheduleDateTime':
                    aValue = new Date(a.scheduleDateTime).getTime();
                    bValue = new Date(b.scheduleDateTime).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [scheduledMessages, sortField, sortDirection]);

    const selectedScheduledTasks = useMemo(
        () => scheduledMessages.filter(task => task._id && selectedScheduledTaskIds.includes(task._id)),
        [scheduledMessages, selectedScheduledTaskIds],
    );

    // Selected items whose new time would already be in the past (they would send at the next scheduler run)
    const now = moment();
    const movedIntoPastCount = selectedScheduledTasks.filter(task => shiftDate(task.scheduleDateTime).isBefore(now)).length;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selection and inputs when the dialog is closed
        setSelectedScheduledTaskIds([]);
        setDirection('later');
        setOffsetAmount(1);
        setOffsetUnit('hours');
        setOpen(false);
    };

    const offsetDescription = `${offsetAmount.toLocaleString()} ${offsetAmount === 1 ? offsetUnit.slice(0, -1) : offsetUnit} ${direction}`;

    // Handle adjust action
    const handleAdjust = () => {
        if (offsetAmount < 1 || selectedScheduledTaskIds.length === 0) {
            return;
        }

        dialog
            .confirm({
                title: 'Adjust Priority Messages',
                message: `Move ${selectedScheduledTaskIds.length.toLocaleString()} Priority Messages ${offsetDescription}?${
                    movedIntoPastCount > 0
                        ? ` ${movedIntoPastCount.toLocaleString()} of them will end up in the past and will be sent at the next scheduler run.`
                        : ''
                }`,
                ok: {
                    text: 'Adjust',
                    color: 'primary',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                bulkAdjustScheduledMessages(selectedScheduledTaskIds, signedOffset, offsetUnit)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(`Priority Messages moved ${offsetDescription}`, {
                                variant: 'success',
                            });

                            refetchScheduledMessages();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to adjust Priority Messages', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to adjust Priority Messages', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    const sortLabelSx = {
        fontWeight: 'bold',
        '& .MuiTableSortLabel-icon': {
            color: 'inherit !important',
        },
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title="Priority Message Bulk Adjust" />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Priority Messages whose scheduled time you want to move. Every selected Priority Message will be moved by
                        the same amount, keeping the gaps between them. Useful after the clocks change. You can select all Priority Messages
                        by checking the checkbox in the header.
                    </Typography>

                    <Box sx={{ pl: 2, pr: 2 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Move the selected Priority Messages...
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="By..."
                                    type="number"
                                    size="small"
                                    value={offsetAmount}
                                    onChange={e => setOffsetAmount(Math.max(1, parseInt(e.target.value, 10) || 0))}
                                    fullWidth
                                    InputProps={{
                                        inputProps: { min: 1 },
                                    }}
                                    error={offsetAmount < 1}
                                />

                                <Select
                                    value={offsetUnit}
                                    size="small"
                                    onChange={e => setOffsetUnit(e.target.value as PriorityMessageAdjustUnit)}
                                    fullWidth
                                >
                                    <MenuItem value="minutes">Minutes</MenuItem>
                                    <MenuItem value="hours">Hours</MenuItem>
                                    <MenuItem value="days">Days</MenuItem>
                                    <MenuItem value="weeks">Weeks</MenuItem>
                                </Select>

                                <Select
                                    value={direction}
                                    size="small"
                                    onChange={e => setDirection(e.target.value as AdjustDirection)}
                                    fullWidth
                                >
                                    <MenuItem value="later">Later</MenuItem>
                                    <MenuItem value="earlier">Earlier</MenuItem>
                                </Select>
                            </Stack>
                        </Stack>
                    </Box>

                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedScheduledTaskIds.length.toLocaleString()} Priority Messages selected
                    </Typography>

                    {movedIntoPastCount > 0 && (
                        <Box sx={{ pl: 2, pr: 2 }}>
                            <Alert severity="warning">
                                {movedIntoPastCount.toLocaleString()} selected Priority Messages would move into the past and will be sent
                                at the next scheduler run.
                            </Alert>
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(204, 223, 227)',
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedScheduledTaskIds.length > 0 &&
                                                    selectedScheduledTaskIds.length < sortedScheduledTasks.length
                                                }
                                                checked={
                                                    sortedScheduledTasks.length > 0 &&
                                                    selectedScheduledTaskIds.length === sortedScheduledTasks.length
                                                }
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedScheduledTaskIds(
                                                            sortedScheduledTasks
                                                                .map(task => task._id)
                                                                .filter((id): id is string => Boolean(id)),
                                                        );
                                                    } else {
                                                        setSelectedScheduledTaskIds([]);
                                                    }
                                                }}
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={sortLabelSx}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'scheduleDateTime'}
                                                direction={sortField === 'scheduleDateTime' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('scheduleDateTime')}
                                                sx={sortLabelSx}
                                            >
                                                Scheduled Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>New Date</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedScheduledTasks.map(scheduledTask => {
                                        const id = scheduledTask._id || '';
                                        const isSelected = selectedScheduledTaskIds.includes(id);

                                        return (
                                            <TableRow
                                                key={id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                    backgroundColor: theme =>
                                                        isSelected ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                                                }}
                                            >
                                                <TableCell align="center">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            if (e.target.checked) {
                                                                setSelectedScheduledTaskIds([...selectedScheduledTaskIds, id]);
                                                            } else {
                                                                setSelectedScheduledTaskIds(
                                                                    selectedScheduledTaskIds.filter(selectedId => selectedId !== id),
                                                                );
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.primary" noWrap>
                                                        {scheduledTask.payload?.name || 'No Name'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {moment(scheduledTask.scheduleDateTime).format(dateFormat)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        color={isSelected ? 'primary.main' : 'text.disabled'}
                                                        fontWeight={isSelected ? 'bold' : 'normal'}
                                                        noWrap
                                                    >
                                                        {shiftDate(scheduledTask.scheduleDateTime).format(dateFormat)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    loading={bulkAdjustScheduledMessagesLoading}
                    disabled={selectedScheduledTaskIds.length === 0 || offsetAmount < 1}
                    onClick={() => {
                        handleAdjust();
                    }}
                >
                    <span>Adjust Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default MessageUsersPriorityMessageBulkAdjustDialog;
